import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFetchURL } from "shared/config/setting";

import LogViewer from "shared/components/ui/LogViewer";
import { useUserData } from "shared/contexts/UserDataProviderNew";
import { useServerConnection } from "shared/contexts/ServerConnectionContext";
import RobotCard from "./robot-card";
import { getRobotEvent, ROBOT_EVENTS, useEventSubscription } from "shared/services/eventBus";
import logger from "shared/utils/logging";

const RobotsDashboard = () => {
  const { t, translationReady } = useTranslation();
  const [connectionLogRecords, setConnectionLogRecords] = useState([]);

  const {
    robotListLoading: isPending,
    updateRobotClientConnectionData,
    handleRobotClientStatusChanged,
    error,
  } = useUserData();

  const { connectionState, CONNECTION_STATES } = useServerConnection();

  const ready = useMemo(() => connectionState === CONNECTION_STATES.CONNECTED, [connectionState, CONNECTION_STATES]);

  const robotConnectedEventPath = useMemo(() => getRobotEvent("*", ROBOT_EVENTS.CONNECTED), []);
  const robotDisconnectedEventPath = useMemo(() => getRobotEvent("*", ROBOT_EVENTS.DISCONNECTED), []);
  const cnnPeersUpdatedEventPath = useMemo(() => getRobotEvent("*", ROBOT_EVENTS.PEERS_UPDATED), []);
  const p2pEventPath = useMemo(() => getRobotEvent("*", ROBOT_EVENTS.P2P_EVENT), []);

  useEventSubscription(
      robotConnectedEventPath,
      (evt) => {
        robotConnectedHandler(evt);
      },
      {},
      []
    );

  useEventSubscription(
      robotDisconnectedEventPath,
      (evt) => {
        robotDisconnectedHandler(evt);
      },
      {},
      []
    );

  useEventSubscription(
      cnnPeersUpdatedEventPath,
      (evt) => {
        robotConnectionPeersUpdatedHandler(evt);
      },
      {},
      []
    );

  useEventSubscription(
      p2pEventPath,
      (evt) => {
        peer2PeerEventHandler(evt);
      },
      {},
      []
    );  

  const robotConnectedHandler = (evt) => {
    logger.log("EVENT: robot connected event: ", evt);
    handleRobotClientStatusChanged(evt, "online");
    addLogRecord(
      "'" + evt.id + "' " + t("dashboard.robotConnected") + ". ", //+ JSON.stringify(robot)
    );
  };

  const robotConnectionPeersUpdatedHandler = (evt) => {
    logger.log("EVENT: cnn_peers_updated event: ", evt);
    const robot_client_id = evt["robot_client_id"];
    const connecting_user_clients = evt["connecting_user_clients"] || [];
    const updated_data = { connecting_user_clients };

    updateRobotClientConnectionData(robot_client_id, connecting_user_clients);

    addLogRecord(
      "'" +
        robot_client_id +
        "' " +
        t("dashboard.peersUpdated") +
        ": " +
        JSON.stringify(updated_data),
    );
  };

  const robotDisconnectedHandler = (evt) => {
    logger.log("Robot disconnected event: ", evt);
    handleRobotClientStatusChanged(evt, "offline");
    addLogRecord(
      "'" + evt.id + "' " + t("dashboard.robotDisconnected") + ". ", //+ JSON.stringify(robot)
    );
  };

  const peer2PeerEventHandler = (evt) => {
    logger.log("Peer2peer event: ", evt);
    const robot_client_id = evt["robot_client_id"];
    const user_client_id = evt["user_client_id"];
    const event_type = evt["event_type"];
    const event_data = evt["event_data"];
    const updated_data = evt["updated_data"] || {};

    const payload = { robot_client_id, updated_data };

    updateRobotClientConnectionData(robot_client_id, updated_data);

    addLogRecord(
      "'" +
        event_type +
        ": " +
        user_client_id +
        " <-> " +
        robot_client_id +
        "' " +
        JSON.stringify(event_data) +
        " updated_data: " +
        JSON.stringify(updated_data),
    );
  };

  const addLogRecord = (msg) => {
    const time = new Date();
    const logRecord = { Time: time, Message: msg };
    setConnectionLogRecords((prev) => {
      return [...prev, logRecord];
    });
  };

  const restart_robot = (robot) => {
    logger.log("restart robot: ", robot);
    const robot_id = robot.id;

    fetch(getFetchURL("/restart-robot"), {
      body: JSON.stringify({
        cmd: "command",
        type: "restart_robot",
        robot_id: robot_id,
      }),
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
    })
      .then((response) => {
        if (!response.ok)
          throw Error(t("dashboard.restartError") + " " + robot_id + '"');
        return response.json();
      })
      .then((data) => {
        if (data["result"] === "success") {
          alert(
            t("robots.title") +
              " " +
              robot_id +
              " " +
              t("dashboard.robotRestarting"),
          );
          logger.log("restarting robot_id: ", robot_id);

          // logger.log("robots before delete:\n", robots);
          // const arr = robots.filter((rb) => {
          //   return rb.id !== robot_id;
          // });
          // setData(arr);
          // logger.log("robots after delete:\n", arr);
        } else {
          alert(
            t("dashboard.restartResult") +
              ": " +
              data["result"] +
              "\n" +
              data["msg"],
          );
        }
      })
      .catch((err) => {
        if (err.name === "AbortError") {
        } else {
          alert(err.message);
        }
      });
  };

  const shutdown_robot = (robot) => {
    logger.log("shutdown robot: ", robot);
    const robot_id = robot.id;

    fetch(getFetchURL("/disconnect"), {
      body: JSON.stringify({
        cmd: "command",
        type: "shutdown_robot",
        robot_id: robot_id,
      }),
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
    })
      .then((response) => {
        if (!response.ok)
          throw Error(t("dashboard.shutdownError") + " " + robot_id + '"');
        return response.json();
      })
      .then((data) => {
        if (data["result"] === "success") {
          handleRobotClientStatusChanged({ id: robot_id }, "offline"); // TODO: fix this, should be handled by the server event, not by the client
          alert(
            t("robots.title") +
              " " +
              robot_id +
              " " +
              t("dashboard.robotShutdown"),
          );
          logger.log("shutdown robot_id: ", robot_id);
        } else {
          alert(
            t("dashboard.shutdownResult") +
              ": " +
              data["result"] +
              "\n" +
              data["msg"],
          );
        }
      })
      .catch((err) => {
        if (err.name === "AbortError") {
        } else {
          alert(err.message);
        }
      });
  };

  return (
    <div>
      {error && <div> {error}</div>}
      {isPending && <div>{t("common.loading")}</div>}
      <div>
        {t("dashboard.connectionStatus")}:{" "}
        {(ready && t("dashboard.ready")) || (!ready && t("dashboard.notReady"))}
      </div>
      {/* {robots && (
        <RobotsCard
          robots={robots}
          shutdownRobotClick={shutdown_robot}
          restartRobotClick={restart_robot}
          ShowOfflineRobots={true}
        ></RobotsCard>
      )} */}
      <RobotCard
        shutdownRobotClick={shutdown_robot}
        restartRobotClick={restart_robot}
        ShowOfflineRobots={true}
      ></RobotCard>

      {/* {robots && (
        <RobotsList
          robots={robots}
          shutdownRobotClick={shutdown_robot}
        ></RobotsList>
      )} */}
      <div>
        {t("dashboard.connectionEventLog")}:
        {/* {JSON.stringify(connectionLogRecords)} */}
      </div>
      <LogViewer LogRecords={connectionLogRecords}></LogViewer>
    </div>
  );
};

export default RobotsDashboard;
